import { APICompsSchema } from "@unit/apis";
import { useAtom } from "jotai";
import React, { useEffect } from "react";
import { useQueries } from "react-query";

import { useAreaApi } from "@/custom-hooks/apis/use-area-api";
import { useGenreApi } from "@/custom-hooks/apis/use-genre-api";
import { useSpotlightApi } from "@/custom-hooks/apis/use-spotlight-api";
import { useStoreApi } from "@/custom-hooks/apis/use-store-api";
import { useNotification } from "@/custom-hooks/firestore/use-notification";
import { useRoom } from "@/custom-hooks/firestore/use-room";
import { useSpotlightState } from "@/custom-hooks/jotai/use-spotlight-state";
import { useAuthentication } from "@/custom-hooks/use-authentication";
import { authUserAtom, unreadInfluencerTicketIdListAtom, unreadNotificationCountAtom } from "@/global-state/jotai-atom";
import { LocalStorageKeys } from "@/global-state/localstorage-keys";
import { QueryKeys } from "@/global-state/react-query-keys";

type Props = {
  children: React.ReactNode;
};
const GlobalJotaiProvider: React.FC<Props> = (props) => {
  const [authUser] = useAtom(authUserAtom);
  const [, setUnreadInfluencerTicketIdList] = useAtom(unreadInfluencerTicketIdListAtom);
  const [, setUnreadNotificationCount] = useAtom(unreadNotificationCountAtom);
  const { getUnreadInfluencerTicketIdList } = useRoom();
  const { getUnreadNotificationCount } = useNotification();
  const { setSpotlightList } = useSpotlightState();
  const { getAreaList } = useAreaApi();
  const { getGenreList } = useGenreApi();
  const { fetchStoreList, fetchFavoriteStoreList } = useStoreApi();
  const { resetAuth } = useAuthentication();
  const { fetchSpotlightList } = useSpotlightApi();

  const results = useQueries([
    {
      queryKey: QueryKeys.getGenreList,
      queryFn: () => getGenreList(),
    },
    {
      queryKey: QueryKeys.getAreaList,
      queryFn: () => getAreaList(),
    },
    {
      queryKey: [QueryKeys.fetchStoreList, "all"],
      queryFn: () => fetchStoreList({ offset: 0, limit: 2000, sortBy: "updatedAt-desc" }),
      enabled: authUser?.status === "active",
    },
    {
      queryKey: [QueryKeys.fetchFavoriteStoreList, "all"],
      queryFn: () => fetchFavoriteStoreList({ offset: 0, limit: 2000, sortBy: "updatedAt-desc" }),
      enabled: authUser?.status === "active",
    },
    {
      queryKey: QueryKeys.fetchSpotlightList,
      queryFn: () => fetchSpotlightList({ offset: 0, limit: 20 }),
      onSuccess: (res: any) => setSpotlightList(res.objects),
    },
    {
      queryKey: QueryKeys.fecthAllRoom,
      enabled: authUser?.status === "active",
      queryFn: () => getUnreadInfluencerTicketIdList(),
      onSuccess: (idList: APICompsSchema["UUIDValue"][]) => setUnreadInfluencerTicketIdList(idList),
    },
    {
      queryKey: QueryKeys.fetchUnreadNotificationCount,
      enabled: authUser?.status === "active",
      queryFn: () => getUnreadNotificationCount(),
      onSuccess: (unreadNotificationCount: number) => setUnreadNotificationCount(unreadNotificationCount),
    },
  ]);

  useEffect(() => {
    (async () => {
      if (results.some((api) => api.error)) {
        localStorage.setItem(LocalStorageKeys.LOCAL_STORAGE_ACCESS_TOKEN_KEY, "");
        localStorage.setItem(LocalStorageKeys.LOCAL_STORAGE_ACCESS_TOKEN_EXPIRES_AT_KEY, "");
        await resetAuth();
      }
    })();
  }, [results]);

  if (results.some((api) => api.error)) {
    return <h4>Error...</h4>;
  }

  return <>{props.children}</>;
};

export default GlobalJotaiProvider;
